import { useMemo } from 'react'

import { useParams } from 'react-router'

import { Page } from '@/components/layout/page/page.tsx'
import { useDateRangeContext } from '@/components/provider/date-range-provider.tsx'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { WithRefresher } from '@/components/ui/with-refresher.tsx'
import { useCreators } from '@/modules/creators/hooks/useCreators.ts'
import { CreatorCombobox } from '@/modules/overview/ui/CreatorCombobox.tsx'
import { DetailedChatterLeaderboardTable } from '@/modules/overview/ui/leaderboard/detailed-chatter-leaderboard-table.tsx'
import { ChatterInspectCards } from '@/modules/tracking/chatter-tracking/components/ChatterInspectCards.tsx'
import {
  mapChatterKeystrokes,
  mapChatterOnlineTime,
  mapChatterPpvPurchase,
  mapChatterSentMessages,
  mapChatterTotalSales,
  mapStatisticsData,
} from '@/modules/tracking/chatter-tracking/utils.ts'

import { useChatterLeaderboardInspectData } from '../../hook/useChatterLeaderboardInspectData.ts'

export const ChatterLeaderboardInspectView = () => {
  const { date, setDate, isOneDay } = useDateRangeContext()
  const {
    selection: {
      allSelect,
      allRemove,
      addSelect,
      removeSelect,
      selectableCreators,
      selectedCreators,
    },
  } = useCreators()
  const parameters = useParams()
  // Quick fix for edge case...
  const {
    data: response,
    isFetching,
    refetch,
  } = useChatterLeaderboardInspectData(
    selectedCreators,
    date,
    parameters.userId ? parseInt(parameters.userId) : undefined,
    parameters.name && !parameters.userId
      ? decodeURI(parameters.name || '').replace('[slash]', '/')
      : undefined,
    isOneDay,
  )

  const { timezoneOffset, name, data } = response ?? {}

  const {
    totalSalesData,
    ppvPurchaseData,
    sentMessagesData,
    keystrokesData,
    onlineTimeData,
    statisticsData,
  } = useMemo(() => {
    return {
      totalSalesData: mapChatterTotalSales(data, isOneDay),
      ppvPurchaseData: mapChatterPpvPurchase(data, isOneDay),
      sentMessagesData: mapChatterSentMessages(data, isOneDay),
      keystrokesData: mapChatterKeystrokes(data, isOneDay),
      onlineTimeData: mapChatterOnlineTime(data),
      statisticsData: mapStatisticsData(data),
    }
  }, [data, isOneDay])

  return (
    <Page
      backToPage={{
        name: 'Overview',
        url: '/overview',
      }}
      title={`Chatter Stats ${name ? `(${name})` : ''}`}
      headerOptions={
        <>
          <CreatorCombobox
            selectable={selectableCreators}
            selected={selectedCreators}
            onAllSelect={allSelect}
            onAllRemove={allRemove}
            onAddSelect={addSelect}
            onRemoveSelect={removeSelect}
          />
          <WithRefresher
            onRefresh={refetch}
            isFetching={isFetching}
          >
            <CalendarRangeDatePicker
              date={date}
              setDate={setDate}
              dateNameSuffix={
                timezoneOffset ? `(${timezoneOffset})` : undefined
              }
            />
          </WithRefresher>
        </>
      }
    >
      {isFetching && <Skeleton className="h-[400px] w-full" />}
      {!isFetching && (
        <div className="grid gap-6">
          <ChatterInspectCards
            totalSalesData={totalSalesData}
            ppvPurchaseData={ppvPurchaseData}
            sentMessagesData={sentMessagesData}
            keystrokesData={keystrokesData}
            onlineTimeData={onlineTimeData}
            isFetching={isFetching}
          />
          <DetailedChatterLeaderboardTable
            data={statisticsData ?? []}
            isFetching={isFetching}
            isOneDay={isOneDay}
          />
        </div>
      )}
    </Page>
  )
}
